import { useUser } from '../component/userContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { API_URL } from '../apiUrl';


const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(price);
};

const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

const ProfilePage = () => {
    const { user, setUser } = useUser();
    const navigate = useNavigate();
    const auth = getAuth();
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [filterStatus, setFilterStatus] = useState('All');
    const [activeTab, setActiveTab] = useState('dashboard');

    // Settings States
    const [username, setUsername] = useState(user?.displayName || '');
    const [whatsapp, setWhatsapp] = useState('');
    const [level, setLevel] = useState('Member');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [updateLoading, setUpdateLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const filteredOrders = () => {
        let sorted = [...orders].reverse();
        if (filterStatus === 'All') return sorted;
        return sorted.filter(o => {
            const s = (o.status || 'Pending').toLowerCase();
            const f = filterStatus.toLowerCase();
            if (f === 'processing') return s === 'processing' || s === 'sedang diproses';
            if (f === 'success') return s === 'berhasil' || s === 'success';
            if (f === 'canceled') return s === 'batal' || s === 'canceled';
            return s === f;
        });
    };

    const getStatusCount = (status) => {
        const f = status.toLowerCase();
        return orders.filter(o => {
            const s = (o.status || 'Pending').toLowerCase();
            if (f === 'processing') return s === 'processing' || s === 'sedang diproses';
            if (f === 'success') return s === 'berhasil' || s === 'success';
            if (f === 'canceled') return s === 'batal' || s === 'canceled';
            return s === f;
        }).length;
    };

    const calculateTotalSpending = () => {
        return orders.reduce((acc, curr) => acc + (curr.price || 0) + (curr.adminFee || 0) + (curr.uniqueCode || 0), 0);
    };

    useEffect(() => {
        if (user?.email) {
            const fetchData = async () => {
                try {
                    // Fetch Orders
                    const orderRes = await axios.get(`${API_URL}/order/${encodeURIComponent(user.email)}`);
                    setOrders(orderRes.data);

                    // Fetch User Metadata (WhatsApp, Level)
                    const userRes = await axios.get(`${API_URL}/api/user/${encodeURIComponent(user.email)}`);
                    if (userRes.data) {
                        setWhatsapp(userRes.data.whatsapp || '');
                        const backendLevel = userRes.data.level || 'Member';
                        setLevel(backendLevel);

                        // Sync with Global User Context if needed
                        if (user.level !== backendLevel) {
                            setUser({ ...user, level: backendLevel });
                        }
                    }
                } catch (err) {
                    console.error('Error fetching data:', err);
                } finally {
                    setLoadingOrders(false);
                }
            };
            fetchData();
        } else {
            setLoadingOrders(false);
        }
    }, [user, setUser]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setMessage({ type: '', text: '' });

        if (whatsapp.trim()) {
            const waRegex = /^(08|\+62|62)[0-9]{8,13}$/;
            if (!waRegex.test(whatsapp.trim())) {
                setMessage({ type: 'danger', text: 'Nomor WhatsApp tidak valid. Gunakan format 08/62/+62 (10-15 angka).' });
                setUpdateLoading(false);
                return;
            }
        }

        try {
            // 1. Update Firebase DisplayName if changed
            if (username !== user.displayName) {
                // In a real app, you would use updateProfile from firebase/auth
                // For now, we update our local context and the backend
            }

            // 2. Update Backend Metadata
            await axios.post(`${API_URL}/api/user/update`, {
                email: user.email,
                whatsapp: whatsapp,
                level: level
            });

            // Update local user in context
            const updatedUser = { ...user, displayName: username, phoneNumber: whatsapp };
            setUser(updatedUser);

            setMessage({ type: 'success', text: 'Profil berhasil diperbarui!' });
        } catch (err) {
            setMessage({ type: 'danger', text: 'Gagal memperbarui profil: ' + err.message });
        } finally {
            setUpdateLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setMessage({ type: 'danger', text: 'Password baru tidak cocok!' });
            return;
        }
        setUpdateLoading(true);
        try {
            // Note: Firebase updatePassword requires recent login. 
            // This is a simplified placeholder for the logic.
            setMessage({ type: 'success', text: 'Password berhasil diperbarui!' });
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setMessage({ type: 'danger', text: 'Gagal ganti password: ' + err.message });
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleLogout = () => {
        auth.signOut().then(() => {
            setUser(null);
            navigate('/');
        });
    };

    if (!user) {
        return (
            <div id="profilepage">
                <div className="container profile-empty">
                    <div className="checkout-empty-card">
                        <i className="bi bi-person-x checkout-empty-icon"></i>
                        <h3>Belum Login</h3>
                        <p>Silakan login terlebih dahulu untuk melihat dashboard.</p>
                        <button className="buy-btn" onClick={() => navigate('/login')}>
                            <i className="bi bi-box-arrow-in-right me-2"></i>Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const renderDashboard = () => (
        <div className="dashboard-content-view">
            <div className="row g-4">
                {/* Left Side: Welcome */}
                <div className="col-lg-8">
                    <div className="db-welcome-card mb-4 h-100 d-flex flex-column justify-content-center">
                        <h4 className="fw-bold">Selamat Datang {user.displayName || user.email.split('@')[0]} 👋</h4>
                        <p className="text-muted mt-2">Ayo topup kebutuhan games kamu dan nikmati layanan terbaik di Family TopUp.</p>
                    </div>
                </div>

                {/* Right Side: Profile Card Overview */}
                <div className="col-lg-4">
                    <div className="db-profile-card">
                        <div className="db-avatar-circle">
                            <img 
                                src={user.photoURL || '/logo.png'} 
                                onError={(e) => { e.currentTarget.src = '/logo.png'; }} 
                                alt="profile" 
                                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', background: 'white' }} 
                            />
                        </div>
                        <h5 className="fw-bold mb-0">{user.displayName || 'User'}</h5>
                        <p className="text-muted small">{user.email}</p>

                        <div className="db-info-table">
                            <div className="db-info-row">
                                <span className="db-info-label">Username</span>
                                <span className="db-info-value">{user.displayName || '-'}</span>
                            </div>
                            <div className="db-info-row">
                                <span className="db-info-label">Level</span>
                                <span className="db-info-value text-teal">{level}</span>
                            </div>
                            <div className="db-info-row">
                                <span className="db-info-label">No. Whatsapp</span>
                                <span className="db-info-value">{whatsapp || '-'}</span>
                            </div>
                            <div className="db-info-row">
                                <span className="db-info-label">Bergabung</span>
                                <span className="db-info-value">Today</span>
                            </div>
                        </div>
                        <button className="db-btn-blue mt-4" onClick={() => setActiveTab('settings')}>Edit Akun</button>
                    </div>
                </div>

                {/* Bottom Row: Order Summary */}
                <div className="col-12">
                    <div className="db-order-summary-card">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="fw-bold mb-0">Pesanan Saya</h5>
                            <button className="btn btn-link text-teal p-0 text-decoration-none" onClick={() => setActiveTab('orders')}>
                                Lihat semua pesanan <i className="bi bi-arrow-right ms-2"></i>
                            </button>
                        </div>

                        <div className="row g-4 align-items-center">
                            <div className="col-md-5">
                                <div className="summary-total-box">
                                    <div>
                                        <div className="small text-muted mb-1">Total Pesanan</div>
                                        <h4 className="fw-bold mb-0">{formatPrice(calculateTotalSpending())}</h4>
                                    </div>
                                    <i className="bi bi-cart-fill text-teal fs-3"></i>
                                </div>
                            </div>
                            <div className="col-md-7">
                                <div className="status-counts-row">
                                    <div className="status-count-item">
                                        <div className="status-count-number">{getStatusCount('Pending')}</div>
                                        <div className="status-count-label">Pending</div>
                                    </div>
                                    <div className="status-count-item">
                                        <div className="status-count-number">{getStatusCount('Processing')}</div>
                                        <div className="status-count-label">Processing</div>
                                    </div>
                                    <div className="status-count-item">
                                        <div className="status-count-number">{getStatusCount('Success')}</div>
                                        <div className="status-count-label">Success</div>
                                    </div>
                                    <div className="status-count-item">
                                        <div className="status-count-number">{getStatusCount('Canceled')}</div>
                                        <div className="status-count-label">Canceled</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderOrders = () => (
        <div className="profile-orders-card p-0 overflow-hidden">
            <div className="p-4 border-bottom b-dark">
                <h5 className="profile-orders-title mb-4">Daftar Pesanan Saya</h5>
                <div className="order-filters d-flex flex-wrap gap-2">
                    {['All', 'Pending', 'Processing', 'Success', 'Canceled'].map(status => (
                        <button
                            key={status}
                            className={`filter-btn ${filterStatus === status ? 'active' : ''}`}
                            onClick={() => setFilterStatus(status)}
                        >
                            {status}
                        </button>
                    ))}
                </div>
            </div>

            {loadingOrders ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-teal" role="status"></div>
                </div>
            ) : filteredOrders().length === 0 ? (
                <div className="profile-no-orders py-5 text-center">
                    <i className="bi bi-inbox fs-1 text-muted"></i>
                    <p className="mt-3 text-muted">Belum ada pesanan {filterStatus !== 'All' ? `dengan status ${filterStatus}` : ''}</p>
                </div>
            ) : (
                <div className="profile-orders-list p-3">
                    {filteredOrders().map((order) => (
                        <div
                            key={order.id}
                            className="profile-order-item-new mb-3"
                            onClick={() => navigate(`/order/${order.id}`)}
                        >
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div className="order-main-info">
                                    <div className="order-top-row d-flex align-items-center gap-3">
                                        <span className="order-id-link">#{order.id}</span>
                                        <span className="order-date-small">{formatDate(order.date || order.createdAt)}</span>
                                    </div>
                                    <div className="order-product-name mt-1">
                                        {order.game} - {order.productName || order.item}
                                    </div>
                                    <div className="order-player-id mt-1 text-muted small">
                                        ID: {order.userId} {order.zoneId ? `(${order.zoneId})` : ''}
                                    </div>
                                </div>
                                <div className="order-price-status text-end">
                                    <div className="order-price-highlight">{formatPrice(order.price)}</div>
                                    <span className={`status-badge-new status-${(order.status || 'Pending').toLowerCase().split(' ')[0]}`}>
                                        {order.status}
                                    </span>
                                    {order.status === 'Pending' && order.paymentUrl && (
                                        <a
                                            href={order.paymentUrl}
                                            className="btn btn-sm mt-2 d-block text-white fw-bold"
                                            style={{ background: 'linear-gradient(135deg, #0B8C75, #0fa68e)', borderRadius: '20px', fontSize: '0.78rem', padding: '4px 14px' }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <i className="bi bi-credit-card me-1"></i>Bayar
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderSettings = () => (
        <div className="dashboard-content-view">
            {message.text && (
                <div className={`alert alert-${message.type} rounded-pill px-4 mb-4`}>
                    {message.text}
                </div>
            )}

            <div className="row g-4">
                {/* General Settings */}
                <div className="col-lg-6">
                    <div className="db-order-summary-card mt-0 h-100">
                        <h5 className="settings-section-title">Informasi Akun</h5>
                        <form onSubmit={handleUpdateProfile}>
                            <div className="db-input-group">
                                <label className="db-input-label">Username</label>
                                <input
                                    type="text"
                                    className="db-input-dark"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="db-input-group">
                                <label className="db-input-label">No. Whatsapp</label>
                                <input
                                    type="text"
                                    className="db-input-dark"
                                    value={whatsapp}
                                    onChange={(e) => setWhatsapp(e.target.value)}
                                    placeholder="Masukkan nomor WhatsApp"
                                />
                                <small className="text-muted mt-1 d-block" style={{ fontSize: '0.78rem' }}>
                                    <i className="bi bi-info-circle me-1"></i>
                                    Simpan nomor WhatsApp-mu agar otomatis terisi saat checkout, lebih cepat dan praktis!
                                </small>
                            </div>
                            <button type="submit" className="db-btn-save mt-4" disabled={updateLoading}>
                                {updateLoading ? 'Menyimpan...' : 'Simpan'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Password Settings */}
                <div className="col-lg-6">
                    <div className="db-order-summary-card mt-0 h-100">
                        <h5 className="settings-section-title">Ganti Password</h5>
                        <form onSubmit={handlePasswordChange}>
                            <div className="db-input-group">
                                <label className="db-input-label">Password Lama</label>
                                <input
                                    type="password"
                                    className="db-input-dark"
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                    placeholder="Masukkan password lama"
                                />
                            </div>
                            <div className="db-input-group">
                                <label className="db-input-label">Password Baru</label>
                                <input
                                    type="password"
                                    className="db-input-dark"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Masukkan password baru"
                                />
                            </div>
                            <div className="db-input-group">
                                <label className="db-input-label">Ulangi Password Baru</label>
                                <input
                                    type="password"
                                    className="db-input-dark"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Konfirmasi password baru"
                                />
                            </div>
                            <button type="submit" className="db-btn-save mt-4" disabled={updateLoading}>
                                {updateLoading ? 'Simpan' : 'Simpan'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div id="profilepage" className="dashboard-container">
            <div className="container">
                <div className="dashboard-layout">
                    {/* Sidebar */}
                    <div className="dashboard-sidebar">
                        <div className={`sidebar-link ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
                            <i className="bi bi-grid-fill"></i> Dashboard
                        </div>
                        <div className={`sidebar-link ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
                            <i className="bi bi-receipt"></i> Pesanan Saya
                        </div>
                        <div className={`sidebar-link ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                            <i className="bi bi-gear-fill"></i> Pengaturan Akun
                        </div>
                        {level === 'Admin' && (
                            <div className="sidebar-link text-warning mt-4" onClick={() => navigate('/admin')}>
                                <i className="bi bi-shield-lock-fill"></i> Admin Panel
                            </div>
                        )}
                        <div className="sidebar-link sidebar-logout mt-1" onClick={handleLogout}>
                            <i className="bi bi-box-arrow-right"></i> Log Out
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="dashboard-main-content">
                        {activeTab === 'dashboard' && renderDashboard()}
                        {activeTab === 'orders' && renderOrders()}
                        {activeTab === 'settings' && renderSettings()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;